import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Empty, Eyebrow, Modal, PageHead, Spinner, Status, Table } from '../../components/ui'
import { api, wsPath } from '../../lib/api'
import { fmtDate, fmtMoney, rewardText } from '../../lib/format'
import { useT } from '../../lib/i18n'
import { useWs } from './useWs'

interface Row {
  id: string
  status: string
  amount_minor: string
  base_minor: string
  sale_minor: string | null
  currency: string
  hold_reason: string | null
  available_at: string
  created_at: string
  test: boolean
  partner_name: string
  partner_email: string
  external_charge_id: string
  refunded_minor: string
}

const STATUSES = ['', 'pending', 'available', 'on_hold', 'in_payout', 'paid', 'reversed', 'clawed_back']

export function CommissionsPage() {
  const { t, locale } = useT()
  const ws = useWs()
  const [status, setStatus] = useState('')
  const [open, setOpen] = useState<string | null>(null)
  const q = useQuery({
    queryKey: ['commissions', ws, status],
    queryFn: () =>
      api<{ commissions: Row[] }>(wsPath(ws, `/commissions?limit=100${status ? `&status=${status}` : ''}`)),
  })
  const rows = q.data?.commissions ?? []
  return (
    <div>
      <PageHead title={t('commissions')} />
      <div className="mb-4 flex flex-wrap gap-1">
        {STATUSES.map((s) => (
          <button
            key={s}
            type="button"
            className={`tab ${status === s ? 'on' : ''}`}
            onClick={() => setStatus(s)}
          >
            {s ? <Status value={s} /> : t('all')}
          </button>
        ))}
      </div>
      {q.isLoading ? (
        <Spinner />
      ) : !rows.length ? (
        <Empty title={t('emptyCommissions')} text={t('emptyCommissionsText')} />
      ) : (
        <Table head={[t('partner'), t('amount'), t('sale'), t('status'), t('holdEnds'), t('created'), '']}>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>
                <div className="text-ink">{r.partner_name}</div>
                <div className="text-xs text-mute">{r.partner_email}</div>
              </td>
              <td className="mono text-ink">
                {fmtMoney(r.amount_minor, r.currency, locale)}
                {r.amount_minor !== r.base_minor && (
                  <div className="text-[11px] text-mute line-through">
                    {fmtMoney(r.base_minor, r.currency, locale)}
                  </div>
                )}
              </td>
              <td className="mono">{r.sale_minor ? fmtMoney(r.sale_minor, r.currency, locale) : '—'}</td>
              <td>
                <Status value={r.status} /> {r.test && <span className="badge badge-warn ml-1">test</span>}
                {r.hold_reason && <div className="mono mt-1 text-[11px] text-danger">{r.hold_reason}</div>}
              </td>
              <td className="mono text-xs">{fmtDate(r.available_at, locale)}</td>
              <td className="mono text-xs">{fmtDate(r.created_at, locale)}</td>
              <td className="text-right">
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setOpen(r.id)}>
                  {t('explain')}
                </button>
              </td>
            </tr>
          ))}
        </Table>
      )}
      {open && <Explain id={open} onClose={() => setOpen(null)} />}
    </div>
  )
}

/** "Every amount explains itself" (plan, 18.4). */
function Explain({ id, onClose }: { id: string; onClose: () => void }) {
  const { t, locale } = useT()
  const ws = useWs()
  const q = useQuery({
    queryKey: ['commission', ws, id],
    queryFn: () =>
      api<{
        commission: {
          amountMinor: number
          baseMinor: number
          saleMinor: number | null
          currency: string
          status: string
          termsVersion: number
          availableAt: string
          holdReason: string | null
        }
        terms: {
          rewards: { event: string; type: string; bps?: number; amountMinor?: number }[]
          holdDays: number
        }
        group: { id: string; name: string; version: number } | null
        ledger: { id: number; account: string; amountMinor: number; memo: string; createdAt: string }[]
        flags: { rule: string; status: string }[]
        conversion: { externalChargeId: string; refundedMinor: number; provider: string }
      }>(wsPath(ws, `/commissions/${id}`)),
  })
  const d = q.data
  return (
    <Modal open onClose={onClose} title={t('explain')} wide>
      {!d ? (
        <Spinner />
      ) : (
        <div className="grid gap-6">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {[
              [
                t('sale'),
                d.commission.saleMinor != null
                  ? fmtMoney(d.commission.saleMinor, d.commission.currency, locale)
                  : '—',
              ],
              [
                t('rate'),
                d.terms?.rewards.map((r) => rewardText(r, d.commission.currency, locale)).join(' · '),
              ],
              [t('amount'), fmtMoney(d.commission.amountMinor, d.commission.currency, locale)],
              [t('termsVersion'), `v${d.commission.termsVersion}`],
              [
                t('group'),
                d.group ? `${d.group.name} · v${d.group.version}` : t('defaultGroup').split(' · ')[0],
              ],
              [t('holdEnds'), fmtDate(d.commission.availableAt, locale)],
              [t('holdDays'), `${d.terms?.holdDays} ${t('days')}`],
              [t('status'), <Status key="s" value={d.commission.status} />],
              [t('holdReason'), d.commission.holdReason ?? '—'],
            ].map(([k, v]) => (
              <div key={String(k)}>
                <Eyebrow className="mb-1">{k}</Eyebrow>
                <div className="text-sm text-ink">{v}</div>
              </div>
            ))}
          </div>
          <div className="mono text-xs text-mute">
            {d.conversion.provider} · {d.conversion.externalChargeId}
            {d.conversion.refundedMinor
              ? ` · refunded ${fmtMoney(d.conversion.refundedMinor, d.commission.currency, locale)}`
              : ''}
          </div>
          <div>
            <Eyebrow className="mb-2">{t('ledger')}</Eyebrow>
            <table className="table">
              <tbody>
                {d.ledger.map((e) => (
                  <tr key={e.id}>
                    <td className="mono text-xs">{fmtDate(e.createdAt, locale, true)}</td>
                    <td className="mono text-xs">{e.account}</td>
                    <td className={`mono text-xs ${e.amountMinor < 0 ? 'text-danger' : 'text-ok'}`}>
                      {fmtMoney(e.amountMinor, d.commission.currency, locale)}
                    </td>
                    <td className="text-xs text-mute">{e.memo}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Modal>
  )
}
